import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

/**
 * Reusable accordion row for the Settings page.
 *
 * Props:
 *  - icon        ReactNode   (coloured MUI icon)
 *  - title       string
 *  - description string      (one-liner shown under title)
 *  - status      { label: string, color: 'success'|'warning'|'info'|'default'|'error' }
 *  - loading     boolean     (shows spinner instead of chip)
 *  - children    ReactNode   (expanded content)
 *  - defaultExpanded boolean
 *  - actions     ReactNode   (optional — rendered right of status chip, e.g. refresh button)
 */
const SettingsRowShell = ({
  icon,
  title,
  description,
  status,
  loading = false,
  children,
  defaultExpanded = false,
  actions,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '12px !important',
      mb: 1.5,
      '&::before': { display: 'none' },
      overflow: 'hidden',
      transition: 'border-color 0.2s',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        px: 2.5,
        py: 0.5,
        minHeight: 64,
        '& .MuiAccordionSummary-content': {
          alignItems: 'center',
          gap: 2,
          my: 1,
        },
      }}
    >
      {/* Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', flexShrink: 0 }}>
        {icon}
      </Box>

      {/* Title + description */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {title}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {description}
          </Typography>
        )}
      </Box>

      {/* Status chip / spinner + optional actions */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : status ? (
          <Chip
            label={status.label}
            color={status.color || 'default'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, height: 26 }}
          />
        ) : null}
        {actions}
      </Box>
    </AccordionSummary>

    <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

export default SettingsRowShell;
